import React, { useMemo, useState, useRef, useEffect, ChangeEvent, useCallback } from 'react';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './BimloaFilter.module.scss';

export type NumberRangeFilterValue = {
  min?: number;
  max?: number;
};

interface NumberRangeFilterProps {
  min?: number;
  max?: number;
  value: NumberRangeFilterValue;
  onChange: (value: NumberRangeFilterValue) => void;
  unit?: string;
  placeholder?: string;
}

const NumberRangeFilter = ({
  min,
  max,
  value,
  onChange,
  unit,
  placeholder,
}: NumberRangeFilterProps) => {
  const { t } = useAdminTranslation();
  const [v, setV] = useState<NumberRangeFilterValue>({});
  const changeFromValueTimer = useRef<any>();
  const changeToValueTimer = useRef<any>();

  useEffect(() => {
    return () => {
      if (changeFromValueTimer.current) {
        clearTimeout(changeFromValueTimer.current);
      }
      if (changeToValueTimer.current) {
        clearTimeout(changeToValueTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    setV({
      ...value,
    });
  }, [value]);

  const isNumber = (item: any) => {
    return typeof item === 'number';
  };

  const filterNumbers = (arr: any[]) => {
    return arr.filter(n => isNumber(n));
  };

  const getMin = (arr: any[]) => {
    const numbers = filterNumbers(arr);
    return numbers.length > 0 ? Math.min(...numbers) : undefined;
  };

  const getMax = (arr: any[]) => {
    const numbers = filterNumbers(arr);
    return numbers.length > 0 ? Math.max(...numbers) : undefined;
  };

  const fromMin = getMin([min]);
  const fromMax = getMin([max, v?.max]);
  const toMin = getMax([min, v?.min]);
  const toMax = getMax([max]);

  const getNewValue = (event: ChangeEvent<HTMLInputElement>) => {
    const textValue = event.target.value.trim();
    const numberValue = Number(textValue);
    const hasData = !(textValue === '' || isNaN(numberValue));
    const value = hasData ? numberValue : undefined;

    return {
      hasData,
      value,
    };
  };

  const confinesChecking = (
    value: number,
    confines: {
      min?: number;
      max?: number;
    }
  ) => {
    let v = value;

    if (typeof confines.min === 'number' && confines.min > v) {
      v = confines.min;
    } else if (typeof confines.max === 'number' && confines.max < v) {
      v = confines.max;
    }

    return v;
  };

  const onFromChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = getNewValue(event);
    const value = {
      ...v,
      min: newValue.value,
    };
    setV(value);

    if (changeFromValueTimer.current) {
      clearTimeout(changeFromValueTimer.current);
    }

    changeFromValueTimer.current = setTimeout(
      () => {
        if (typeof value.min === 'number') {
          value.min = confinesChecking(value.min, {
            min: fromMin,
            max: fromMax,
          });
        }
        onChange(value);
      },
      typeof value.min === 'number' && typeof value.max === 'number' ? 1000 : 0
    );
  };

  const onToChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = getNewValue(event);
    const value = {
      ...v,
      max: newValue.value,
    };
    setV(value);

    if (changeToValueTimer.current) {
      clearTimeout(changeToValueTimer.current);
    }

    changeToValueTimer.current = setTimeout(
      () => {
        if (typeof value.max === 'number') {
          value.max = confinesChecking(value.max, {
            min: toMin,
            max: toMax,
          });
        }
        onChange(value);
      },
      typeof value.min === 'number' && typeof value.max === 'number' ? 1000 : 0
    );
  };

  const minPlaceholder = placeholder || t('bimloaFilter.min').toString();
  const maxPlaceholder = placeholder || t('bimloaFilter.max').toString();

  return (
    <div className={styles.numberRangeContainer}>
      <input
        type='number'
        min={fromMin}
        max={fromMax}
        value={typeof v.min === 'number' ? v.min : ''}
        placeholder={unit ? `${minPlaceholder} (${unit})` : minPlaceholder}
        onChange={onFromChange}
      />
      <span>{t('bimloaFilter.to')}</span>
      <input
        type='number'
        min={toMin}
        max={toMax}
        value={typeof v.max === 'number' ? v.max : ''}
        placeholder={unit ? `${maxPlaceholder} (${unit})` : maxPlaceholder}
        onChange={onToChange}
      />
    </div>
  );
};

export default NumberRangeFilter;
